import apiClient, { handleApiError } from '../lib/api-client';
import { API_ENDPOINTS } from '../config/api';

export interface SugarReadingDto {
  reading: number;
  mealContext: 'FASTING' | 'BEFORE_MEAL' | 'AFTER_MEAL' | 'RANDOM';
  notes?: string;
}

export interface CalorieEntryDto {
  calories: number;
  foodItems: Array<{
    name: string;
    calories: number;
    carbs: number;
    protein: number;
    fat: number;
  }>;
  imageUrl?: string;
  notes?: string;
}

export interface StepEntryDto {
  steps: number;
  date: string;
}

export interface WeightEntryDto {
  weight: number;
  date: string;
  notes?: string;
}

export interface BloodPressureEntryDto {
  systolic: number;
  diastolic: number;
  date: string;
  notes?: string;
}

export interface MedicationEntryDto {
  name: string;
  dosage: number;
  unit: string;
  type?: string;
  takenAt?: string;
  notes?: string;
}

export interface InsulinEntryDto {
  type: string;
  units: number;
  injectionSite?: string;
  takenAt?: string;
  glucoseBefore?: number;
  glucoseAfter?: number;
  notes?: string;
}

export const healthDataService = {
  async getGoals() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.GOALS);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async createSugarReading(data: SugarReadingDto) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.HEALTH.SUGAR_READINGS, data);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getSugarReadings(params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.SUGAR_READINGS, { params });
      const body = response?.data;
      return (body && typeof body === 'object' && 'data' in body ? body.data : body) ?? [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async createCalorieEntry(data: CalorieEntryDto) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.HEALTH.CALORIE_ENTRIES, data);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getCalorieEntries(params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.CALORIE_ENTRIES, { params });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async createStepEntry(data: StepEntryDto) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.HEALTH.STEP_ENTRIES, data);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getStepEntries(params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.STEP_ENTRIES, { params });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async createWeightEntry(data: WeightEntryDto) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.HEALTH.WEIGHT_ENTRIES, data);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getWeightEntries(params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.WEIGHT_ENTRIES, { params });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async createBloodPressureEntry(data: BloodPressureEntryDto) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.HEALTH.BLOOD_PRESSURE_ENTRIES, data);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getBloodPressureEntries(params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.BLOOD_PRESSURE_ENTRIES, { params });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async createMedicationEntry(data: MedicationEntryDto) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.HEALTH.MEDICATIONS, data);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getMedicationEntries(params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.MEDICATIONS, { params });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async createInsulinEntry(data: InsulinEntryDto) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.HEALTH.INSULIN, data);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getInsulinEntries(params?: { limit?: number; offset?: number; startDate?: string; endDate?: string }) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.INSULIN, { params });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getAnalytics(params?: { startDate?: string; endDate?: string }) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.ANALYTICS, { params });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async exportData(format: 'json' | 'csv' = 'json') {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEALTH.EXPORT, {
        params: { format },
        responseType: format === 'csv' ? 'blob' : 'json',
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

